import type { SplitView, ThreeOffersCheck } from '@epc/contracts';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { date, plural, tenge } from '../../lib/format';

/**
 * Правило трёх КП (ЗК-05) в карточке заявки — до выбора и согласования: нужны ли три КП, сколько есть,
 * заполнено ли обоснование. Суммы — только с уровнем денег (без него сервер их не присылает).
 */
export function ThreeOffersBox({ t }: { t: ThreeOffersCheck }) {
  const basis =
    t.basis === 'offer' ? 'по выбранному КП' : t.basis === 'estimate' ? 'по смете' : 'сметной суммы нет';
  const verdict =
    t.required === false
      ? 'Три КП не нужны: сумма не выше порога'
      : t.offers >= 3
        ? 'Три КП есть'
        : t.reasonFilled
          ? 'КП меньше трёх, обоснование заполнено'
          : t.required === null
            ? 'Нужны три КП или обоснование: сметной суммы нет'
            : 'Нужны три КП или обоснование';
  return (
    <section
      aria-label="Правило трёх КП"
      className={clsx(
        'rounded-md border px-4 py-3 text-[14px]',
        t.ok ? 'border-line' : 'border-warning bg-warning-soft',
      )}
    >
      <div className="font-medium text-ink">Правило трёх КП (ЗК-05)</div>
      <p className="mt-1 text-ink">{verdict}.</p>
      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] sm:grid-cols-4">
        <div>
          <dt className="text-ink-2">Порог компании</dt>
          <dd className="num text-ink">{t.limit === null ? 'скрыт' : tenge(t.limit)}</dd>
        </div>
        <div>
          <dt className="text-ink-2">{t.basis === 'none' ? 'Сумма' : `Сумма ${basis}`}</dt>
          <dd className="num text-ink">
            {t.basis === 'none' ? 'нет' : t.amount === null ? 'скрыта' : tenge(t.amount)}
          </dd>
        </div>
        <div>
          <dt className="text-ink-2">КП в заявке</dt>
          <dd className="num text-ink">{t.offers}</dd>
        </div>
        <div>
          <dt className="text-ink-2">Обоснование без трёх КП</dt>
          <dd className="text-ink">{t.reasonFilled ? 'заполнено' : 'нет'}</dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * Возможное дробление закупки (ЗК-33): с кем и с какими закупками вместе выше порога. Порог — трёх КП компании
 * или лимит согласования того, кто смотрит (ДС-06). Сходные закупки — ссылками, только те, что человек вправе
 * открыть; остальные — числом.
 */
export function SplitBox({ splits, before }: { splits: SplitView[]; before?: boolean }) {
  if (!splits.length) return null;
  const byLimit = splits.some((s) => s.basis === 'approval_limit');
  return (
    <section
      aria-label="Возможное дробление закупки"
      className="rounded-md border border-warning bg-warning-soft px-4 py-3 text-[14px]"
    >
      <div className="font-medium text-ink">
        Возможное дробление закупки (ЗК-33{byLimit ? ', ДС-06' : ''})
      </div>
      <p className="mt-1 text-[13px] text-ink-2">
        {before
          ? 'Поставщик ещё не выбран: показано, с какими КП закупка вместе со сходными у того же поставщика выше порога.'
          : 'Вместе со сходными закупками у того же контрагента сумма выше порога. Согласующие и служба безопасности проверяют, не разбита ли закупка.'}
        {byLimit
          ? ' «Ваш лимит»: каждая закупка в пределах вашего лимита согласования, а вместе выше — решал бы следующий согласующий.'
          : ''}
      </p>
      <ul className="mt-2 flex flex-col gap-3">
        {splits.map((s) => (
          <li key={`${s.basis}:${s.offerId ?? s.supplierName}`}>
            <div className="text-ink">
              {s.supplierName ? <span className="font-medium">{s.supplierName}: </span> : null}
              {plural(s.count, 'сходная закупка', 'сходные закупки', 'сходных закупок')} за {s.windowDays} дн.
              {s.total !== null && s.limit !== null ? (
                <>
                  , вместе <span className="num">{tenge(s.total)}</span>{' '}
                  {s.basis === 'approval_limit' ? 'при вашем лимите' : 'при пороге трёх КП'}{' '}
                  <span className="num">{tenge(s.limit)}</span>
                </>
              ) : s.basis === 'approval_limit' ? (
                ', вместе выше вашего лимита согласования'
              ) : null}
            </div>
            {s.matches.length || s.hidden ? (
              <ul className="mt-1 flex flex-wrap gap-x-4">
                {s.matches.map((m) => (
                  <li key={m.id}>
                    <Link
                      to={m.kind === 'contract' ? `/contracts/${m.id}` : `/procurement/${m.id}`}
                      className="inline-flex min-h-11 items-center text-ink underline underline-offset-4"
                    >
                      {m.kind === 'contract' ? 'Договор' : 'Заявка'} {m.number} от {date(m.at)}
                      {m.amount !== null ? `, ${tenge(m.amount)}` : ''}
                    </Link>
                  </li>
                ))}
                {s.hidden ? (
                  <li className="inline-flex min-h-11 items-center text-ink-2">
                    и ещё {s.hidden} в проектах, к которым у вас нет доступа
                  </li>
                ) : null}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
